import { useEffect, useState, useMemo } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users2, ArrowDownToLine, ArrowUpFromLine, ShieldCheck, Play, Award, Search, Trash2, ArrowRight, Plus, Minus, RotateCcw, Crown, ChevronLeft, Key, ExternalLink, Copy, Eye, EyeOff } from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";

const RANK_COLORS = { core: "#22C55E", prime: "#3B82F6", elite: "#A855F7", supreme: "#EAB308" };

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="nb-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
        <Icon size={16} className="text-nb-cyan"/>
      </div>
      <div className="mt-2 font-mono text-3xl font-bold" style={{color: color || "#F8FAFC"}}>{value}</div>
    </div>
  );
}

/* Company Wallet Balance summary — reflects real-time deposits/withdrawals including auto-verified ones */
function CWBRow({ label, val, color }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="font-mono" style={{color: color || "#F8FAFC"}}>${val.toFixed(2)}</span>
    </div>
  );
}
function CompanyWalletBalance() {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get("/admin/company-wallet-summary"); setData(data); } catch (e) { /* silent */ } };
  useEffect(() => { load(); const iv = setInterval(load, 30_000); return () => clearInterval(iv); }, []);
  if (!data) return null;
  return (
    <div className="mt-6 nb-card p-6" data-testid="company-wallet-balance">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading font-semibold text-white">Company Wallet Balance</div>
        <button onClick={load} className="text-xs text-nb-cyan hover:text-white">Refresh</button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-[#8AE234]/30 bg-[#8AE234]/5 p-4">
          <div className="text-xs uppercase tracking-widest text-[#8AE234]">Deposits</div>
          <div className="font-mono text-2xl text-white mt-1" data-testid="cw-dep-all">${data.deposits.all_time.total.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">All-time · {data.deposits.all_time.count} tx</div>
          <div className="mt-2 space-y-1">
            <CWBRow label="Today" val={data.deposits.today.total} color="#8AE234"/>
            <CWBRow label="Pending" val={data.deposits.pending.total} color="#F59E0B"/>
          </div>
        </div>
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
          <div className="text-xs uppercase tracking-widest text-red-400">Withdrawals</div>
          <div className="font-mono text-2xl text-white mt-1" data-testid="cw-wd-all">${data.withdrawals.all_time.total.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">All-time · {data.withdrawals.all_time.count} tx</div>
          <div className="mt-2 space-y-1">
            <CWBRow label="Today" val={data.withdrawals.today.total} color="#EF4444"/>
            <CWBRow label="Pending" val={data.withdrawals.pending.total} color="#F59E0B"/>
          </div>
        </div>
        <div className="rounded-xl border border-nb-cyan/30 bg-nb-cyan/5 p-4">
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Net Company Balance</div>
          <div className="font-mono text-2xl text-white mt-1" data-testid="cw-net">${data.net_balance.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">Deposits − Withdrawals</div>
        </div>
      </div>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState({});
  const [metrics, setMetrics] = useState({ series: [], bot_distribution: [], rank_distribution: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: m }] = await Promise.all([api.get("/admin/stats"), api.get("/admin/dashboard-metrics?days=14")]);
    setStats(s); setMetrics(m);
  };
  useEffect(() => { load(); }, []);

  const runProfits = async () => {
    setBusy(true);
    try { await api.post("/admin/run-daily-profit"); toast.success("Daily profits credited"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const runLeadership = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/run-leadership-bonus"); toast.success(data.message); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Admin Console</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Overview</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 rounded-lg border border-[#8AE234]/30 bg-[#8AE234]/5 px-3 py-1.5 text-xs text-[#8AE234]" data-testid="auto-profit-status">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8AE234] animate-pulse"/>
            Daily profit runs automatically every 24h
          </div>
          <button disabled={busy} onClick={runLeadership} data-testid="admin-run-leadership" className="nb-btn-secondary text-sm"><Award size={12} className="inline mr-1"/> Leadership bonus</button>
          <button disabled={busy} onClick={async () => { try { const { data } = await api.post("/admin/complete-expired-cycles"); toast.success(data.message); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid="admin-complete-cycles" className="nb-btn-secondary text-sm">Complete expired cycles</button>
          <button disabled={busy} onClick={async () => { if (!window.confirm("Seed 10,000 demo users into the leaderboard? Existing demo users will be replaced.")) return; try { const { data } = await api.post("/admin/seed-demo-users", { count: 10000, replace: true }); toast.success(data.message); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} data-testid="admin-seed-demo" className="nb-btn-secondary text-sm">Seed 10K leaderboard</button>
          <button disabled={busy} onClick={async () => { if (!window.confirm("Retroactively release any Cleared/Target-Met credit that failed to move Hold Profit to the user's real balance. Silent, idempotent, safe to run repeatedly.")) return; setBusy(true); try { const { data } = await api.post("/admin/credit-hold-release-fix"); toast.success(data.message); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }} data-testid="admin-credit-release-fix" className="text-sm px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">Release stuck credit profits</button>
          <button disabled={busy} onClick={async () => { if (!window.confirm("Retroactively fix the credit-leak bug for ALL affected users? This will silently deduct leaked credit principal from their real balances and log a credit_leak_correction audit trail. Safe to run repeatedly.")) return; setBusy(true); try { const { data } = await api.post("/admin/credit-leak-fix"); toast.success(data.message); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } }} data-testid="admin-credit-leak-fix" className="text-sm px-3 py-1.5 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">Fix credit leak</button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat testid="admin-stat-users" label="Total Users" value={stats.total_users||0} icon={Users2}/>
        <Stat testid="admin-stat-active" label="Active Investors" value={stats.active_users||0} icon={Users2} color="#8AE234"/>
        <Stat testid="admin-stat-pending-dep" label="Pending Deposits" value={stats.pending_deposits||0} icon={ArrowDownToLine} color="#F59E0B"/>
        <Stat testid="admin-stat-pending-wd" label="Pending Withdrawals" value={stats.pending_withdrawals||0} icon={ArrowUpFromLine} color="#F59E0B"/>
        <Stat testid="admin-stat-pending-kyc" label="Pending KYC" value={stats.pending_kyc||0} icon={ShieldCheck} color="#00E5FF"/>
        <Stat testid="admin-stat-vol-dep" label="Total Deposits" value={`$${(stats.total_deposits||0).toFixed(2)}`} icon={ArrowDownToLine} color="#8AE234"/>
        <Stat testid="admin-stat-vol-wd" label="Total Withdrawals" value={`$${(stats.total_withdrawals||0).toFixed(2)}`} icon={ArrowUpFromLine} color="#EF4444"/>
      </div>

      {/* Feb-2026: Company Wallet Balance (fixes auto-deposit sync visibility) */}
      <CompanyWalletBalance/>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="nb-card p-6 lg:col-span-2">
          <div className="font-heading text-white font-semibold mb-4">Deposits vs Withdrawals — Last 14 days</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={metrics.series}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3"/>
              <XAxis dataKey="date" tick={{fill: "#64748B", fontSize: 10}} tickFormatter={(d)=>d.slice(5)}/>
              <YAxis tick={{fill: "#64748B", fontSize: 10}}/>
              <Tooltip contentStyle={{background: "#0F1626", border: "1px solid #1f2937", borderRadius: 8, color: "#F8FAFC"}}/>
              <Legend wrapperStyle={{fontSize: 12, color: "#94A3B8"}}/>
              <Bar dataKey="deposits" fill="#8AE234" name="Deposits ($)"/>
              <Bar dataKey="withdrawals" fill="#EF4444" name="Withdrawals ($)"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">New Signups</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={metrics.series}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3"/>
              <XAxis dataKey="date" tick={{fill: "#64748B", fontSize: 10}} tickFormatter={(d)=>d.slice(5)}/>
              <YAxis tick={{fill: "#64748B", fontSize: 10}}/>
              <Tooltip contentStyle={{background: "#0F1626", border: "1px solid #1f2937", borderRadius: 8, color: "#F8FAFC"}}/>
              <Line type="monotone" dataKey="signups" stroke="#00E5FF" strokeWidth={2} dot={{fill: "#00E5FF"}}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">Bot Distribution</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={metrics.bot_distribution} layout="vertical">
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3"/>
              <XAxis type="number" tick={{fill: "#64748B", fontSize: 10}}/>
              <YAxis dataKey="name" type="category" tick={{fill: "#94A3B8", fontSize: 11}} width={110}/>
              <Tooltip contentStyle={{background: "#0F1626", border: "1px solid #1f2937", borderRadius: 8, color: "#F8FAFC"}}/>
              <Bar dataKey="users" fill="#8AE234" name="Active Users"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">Rank Distribution</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={metrics.rank_distribution} dataKey="users" nameKey="rank" cx="50%" cy="50%" outerRadius={80} label={{fill: "#F8FAFC", fontSize: 11}}>
                {(metrics.rank_distribution||[]).map((r, i) => <Cell key={i} fill={RANK_COLORS[r.rank]}/>)}
              </Pie>
              <Tooltip contentStyle={{background: "#0F1626", border: "1px solid #1f2937", borderRadius: 8, color: "#F8FAFC"}}/>
              <Legend wrapperStyle={{fontSize: 12, color: "#94A3B8"}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AppLayout>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [rank, setRank] = useState("all");
  const [kyc, setKyc] = useState("all");
  const nav = useNavigate();

  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data.users); };
  useEffect(() => { load(); }, []);
  const act = async (user_id, action) => {
    try { await api.post("/admin/user-action", { user_id, action }); toast.success("Applied"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const filtered = useMemo(() => users.filter(u => {
    if (u.role === "admin") return false;
    if (rank !== "all" && u.rank !== rank) return false;
    if (kyc !== "all" && u.kyc_status !== kyc) return false;
    if (q) { const qq = q.toLowerCase(); return u.email?.toLowerCase().includes(qq) || u.name?.toLowerCase().includes(qq) || u.referral_code?.toLowerCase().includes(qq); }
    return true;
  }), [users, q, rank, kyc]);

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-3xl text-white">Users <span className="text-slate-500 text-lg font-mono">({filtered.length})</span></h1>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-slate-500"/>
            <input data-testid="user-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search email/name/code" className="nb-input pl-9 py-2 text-sm min-w-[240px]"/>
          </div>
          <select data-testid="user-filter-rank" value={rank} onChange={(e)=>setRank(e.target.value)} className="nb-input py-2 text-sm">
            <option value="all">All ranks</option>
            <option value="core">Core</option><option value="prime">Prime</option><option value="elite">Elite</option><option value="supreme">Supreme</option>
          </select>
          <select data-testid="user-filter-kyc" value={kyc} onChange={(e)=>setKyc(e.target.value)} className="nb-input py-2 text-sm">
            <option value="all">All KYC</option>
            <option value="pending">Pending</option><option value="submitted">Submitted</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      <div className="nb-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
            <tr><th className="text-left px-3 py-3">User</th><th className="text-left px-3 py-3">Referral</th><th className="text-left px-3 py-3">Upline</th><th className="text-left px-3 py-3">Rank</th><th className="text-left px-3 py-3">KYC</th><th className="text-right px-3 py-3">Balance</th><th className="text-right px-3 py-3">Invested</th><th className="text-right px-3 py-3">Credit Facility</th><th className="text-right px-3 py-3">Bot</th><th className="px-3 py-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-white/5">
                <td className="px-3 py-3">
                  <div className="text-white">{u.name} {u.blocked && <span className="text-[10px] text-red-400 ml-1">BLOCKED</span>}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-3 py-3 text-xs font-mono text-nb-cyan">{u.referral_code}</td>
                <td className="px-3 py-3 text-xs">
                  {u.upline ? (
                    <>
                      <div className="text-white truncate max-w-[140px]" title={u.upline.name}>{u.upline.name}</div>
                      <div className="text-nb-cyan font-mono text-[10px]">{u.upline.referral_code}</div>
                    </>
                  ) : <span className="text-slate-500">Root</span>}
                </td>
                <td className="px-3 py-3 text-xs uppercase font-bold" style={{color: RANK_COLORS[u.rank]}}>{u.rank}</td>
                <td className="px-3 py-3 text-xs">{u.kyc_status}</td>
                <td className="px-3 py-3 text-right font-mono text-white">${(u.balance||0).toFixed(2)}</td>
                <td className="px-3 py-3 text-right font-mono text-nb-cyan">${(u.active_investment||0).toFixed(2)}</td>
                <td className="px-3 py-3 text-right text-xs">
                  {u.credit_facility ? (
                    <div className="text-right">
                      <div className="font-mono text-[#EAB308]">${(u.credit_facility.amount||0).toFixed(2)}</div>
                      <div className="text-[10px] text-slate-400">Hold <span className="text-[#8AE234] font-mono">${(u.credit_facility.hold_profit||0).toFixed(2)}</span></div>
                      <div className="text-[10px] text-slate-500">BV {((u.credit_facility.business_volume_generated||0) / (u.credit_facility.business_volume_target||1) * 100).toFixed(0)}%</div>
                    </div>
                  ) : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-3 py-3 text-right text-xs text-slate-400">{u.active_bot_id ? `Bot-${u.active_bot_id}` : "—"}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1 justify-end">
                    <button data-testid={`view-user-${u.id}`} onClick={()=>nav(`/admin/users/${u.id}`)} className="text-xs px-2 py-1 rounded border border-nb-cyan/40 text-nb-cyan hover:bg-nb-cyan/10">View <ArrowRight size={10} className="inline"/></button>
                    <button data-testid={`impersonate-${u.id}`} title="Open user account in new tab" onClick={async ()=>{ try { const { data } = await api.post(`/admin/user-impersonate/${u.id}`); window.open(`${window.location.origin}/impersonate?token=${encodeURIComponent(data.token)}`, "_blank", "noopener,noreferrer"); toast.success(`Opened ${data.user.email} in new tab`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className="text-xs px-2 py-1 rounded border border-nb-cyan bg-nb-cyan/10 text-nb-cyan hover:bg-nb-cyan/20"><ExternalLink size={10} className="inline"/></button>
                    {u.kyc_status==="submitted" && <button data-testid={`kyc-approve-${u.id}`} onClick={()=>act(u.id,"approve_kyc")} className="text-xs px-2 py-1 rounded bg-[#8AE234]/10 text-[#8AE234] border border-[#8AE234]/30">Approve KYC</button>}
                    {u.kyc_status==="submitted" && <button onClick={()=>act(u.id,"reject_kyc")} className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/30">Reject</button>}
                    <button data-testid={`block-${u.id}`} onClick={()=>act(u.id, u.blocked?"unblock":"block")} className="text-xs px-2 py-1 rounded border border-white/10 text-slate-300 hover:text-white">{u.blocked?"Unblock":"Block"}</button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-500">No users found</td></tr>}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

export function AdminUserDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjust, setAdjust] = useState({ amount: "", reason: "", direction: "credit" });
  const [rankOpen, setRankOpen] = useState(false);
  const [newRank, setNewRank] = useState("core");
  const [resetOpen, setResetOpen] = useState(false);
  const [refund, setRefund] = useState(true);
  const [manualDepOpen, setManualDepOpen] = useState(false);
  const [manualDep, setManualDep] = useState({ amount: "", note: "" });
  // Password reset modal
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCustom, setPwCustom] = useState("");
  const [pwResult, setPwResult] = useState(null); // {new_password}
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  // Impersonation
  const [impBusy, setImpBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admin/user/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]);

  const doAdjust = async () => {
    const amt = parseFloat(adjust.amount) * (adjust.direction === "debit" ? -1 : 1);
    try { await api.post("/admin/user-balance-adjust", { user_id: id, amount: amt, reason: adjust.reason }); toast.success("Balance adjusted"); setAdjustOpen(false); setAdjust({ amount: "", reason: "", direction: "credit"}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doRank = async () => {
    try { await api.post("/admin/user-set-rank", { user_id: id, rank: newRank }); toast.success("Rank updated"); setRankOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReset = async () => {
    try { await api.post("/admin/user-reset-bot", { user_id: id, refund_investment: refund }); toast.success("Bot reset"); setResetOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doManualDep = async () => {
    try { await api.post("/admin/manual-deposit", { user_id: id, amount: parseFloat(manualDep.amount), note: manualDep.note || "Manual deposit by admin" }); toast.success("Manual deposit credited"); setManualDepOpen(false); setManualDep({ amount: "", note: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doPasswordReset = async () => {
    setPwBusy(true);
    try {
      const { data } = await api.post("/admin/user-reset-password", { user_id: id, new_password: pwCustom || null });
      setPwResult(data);
      setPwCustom("");
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };
  const copyPassword = async () => {
    if (!pwResult?.new_password) return;
    await navigator.clipboard.writeText(pwResult.new_password);
    toast.success("Password copied to clipboard");
  };
  const doImpersonate = async () => {
    setImpBusy(true);
    try {
      const { data } = await api.post(`/admin/user-impersonate/${id}`);
      // Open the user's account in a new tab. Token flows via URL to /impersonate,
      // which stores it in that tab's sessionStorage (admin's own session is untouched).
      const url = `${window.location.origin}/impersonate?token=${encodeURIComponent(data.token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(`Opened ${data.user?.email} in new tab (60-min session)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImpBusy(false); }
  };

  if (!detail) return <AppLayout variant="admin" requireAdmin><div className="text-slate-500">Loading…</div></AppLayout>;
  const u = detail.user;

  return (
    <AppLayout variant="admin" requireAdmin>
      <Link to="/admin/users" className="text-xs text-nb-cyan hover:text-white flex items-center gap-1 mb-3"><ChevronLeft size={12}/> Back to users</Link>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">{u.name}</h1>
          <div className="text-sm text-slate-400">{u.email} · <span className="font-mono text-nb-cyan">{u.referral_code}</span></div>
          <div className="text-xs text-slate-500 mt-1">Joined {new Date(u.created_at).toLocaleString()}</div>
          {detail.upline && <div className="text-xs text-slate-500 mt-1">Upline: <span className="text-slate-300">{detail.upline.name}</span> <span className="font-mono text-nb-cyan">{detail.upline.referral_code}</span></div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="btn-impersonate" onClick={doImpersonate} disabled={impBusy} className="nb-btn-primary text-sm bg-nb-cyan text-black hover:opacity-90"><ExternalLink size={12} className="inline mr-1"/>{impBusy ? "Opening…" : "Login as user"}</button>
          <button data-testid="btn-reset-password" onClick={()=>{ setPwResult(null); setPwCustom(""); setPwOpen(true); }} className="nb-btn-secondary text-sm"><Key size={12} className="inline mr-1"/>Reset password</button>
          <button data-testid="btn-manual-deposit" onClick={()=>setManualDepOpen(true)} className="nb-btn-secondary text-sm"><ArrowDownToLine size={12} className="inline mr-1"/>Manual Deposit</button>
          <button data-testid="btn-adjust-balance" onClick={()=>setAdjustOpen(true)} className="nb-btn-secondary text-sm"><Plus size={12} className="inline mr-1"/>Adjust balance</button>
          <button data-testid="btn-set-rank" onClick={()=>{ setNewRank(u.rank); setRankOpen(true); }} className="nb-btn-secondary text-sm"><Crown size={12} className="inline mr-1"/>Set rank</button>
          <button
            data-testid="btn-reset-earnings"
            onClick={async () => {
              const cur = {
                total_profit: u.total_profit || 0,
                total_team_income: u.total_team_income || 0,
                total_referral_bonus: u.total_referral_bonus || 0,
                total_leadership_bonus: u.total_leadership_bonus || 0,
                total_shareholder_bonus: u.total_shareholder_bonus || 0,
                total_deposit: u.total_deposit || 0,
                total_withdrawal: u.total_withdrawal || 0,
              };
              const summary = Object.entries(cur).map(([k,v])=>`${k}: $${Number(v).toFixed(2)}`).join("\n");
              const raw = window.prompt(
                `Silent override of earning counters — used to re-anchor the deposit-recovery rule.\n\n` +
                `CURRENT values:\n${summary}\n\n` +
                `Enter new values as JSON. Leave any key out to keep unchanged. Example:\n` +
                `{"total_profit": 3.5, "total_team_income": 0}`,
                JSON.stringify(cur, null, 2),
              );
              if (!raw) return;
              let obj;
              try { obj = JSON.parse(raw); } catch { toast.error("Invalid JSON"); return; }
              const reason = window.prompt("Reason (audit log):", "Deposit-recovery re-anchor") || "";
              if (!window.confirm(`Confirm silent update?\n\n${Object.entries(obj).map(([k,v])=>`${k}: $${Number(v).toFixed(4)}`).join("\n")}\n\n(No user notification.)`)) return;
              try {
                await api.post("/admin/user-earnings-set", { user_id: id, ...obj, reason });
                toast.success("Earning counters updated");
                load();
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }}
            className="nb-btn-secondary text-sm text-[#EAB308] border-[#EAB308]/40"
          >
            <Award size={12} className="inline mr-1"/>Reset earnings
          </button>
          {u.active_bot_id && <button data-testid="btn-reset-bot" onClick={()=>setResetOpen(true)} className="nb-btn-secondary text-sm text-red-400 border-red-500/30"><RotateCcw size={12} className="inline mr-1"/>Reset bot</button>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Stat label="Balance" value={`$${(u.balance||0).toFixed(2)}`} icon={ArrowDownToLine} color="#8AE234"/>
        <Stat label="Active Investment" value={`$${(u.active_investment||0).toFixed(2)}`} icon={Users2}/>
        <Stat label="Total Deposit" value={`$${(u.total_deposit||0).toFixed(2)}`} icon={ArrowDownToLine}/>
        <Stat label="Total Withdrawal" value={`$${(u.total_withdrawal||0).toFixed(2)}`} icon={ArrowUpFromLine}/>
        <Stat label="Total Profit" value={`$${(u.total_profit||0).toFixed(2)}`} icon={Award} color="#00E5FF"/>
        <Stat label="Referral Bonus" value={`$${(u.total_referral_bonus||0).toFixed(2)}`} icon={Users2}/>
        <Stat label="Team Income" value={`$${(u.total_team_income||0).toFixed(2)}`} icon={Users2}/>
        <div className="nb-card p-6">
          <div className="text-xs uppercase tracking-widest text-slate-400">Rank / KYC</div>
          <div className="mt-2 font-mono text-2xl font-bold uppercase" style={{color: RANK_COLORS[u.rank]}}>{u.rank}</div>
          <div className="text-xs text-slate-400 mt-1">KYC: {u.kyc_status}</div>
          <div className="text-xs text-slate-400">Bot: {u.active_bot_id ? `Bot-${u.active_bot_id}` : "None"}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-3">Direct Referrals ({detail.directs.length})</div>
          <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
            {detail.directs.length === 0 && <div className="text-slate-500 text-sm py-3 text-center">No direct referrals</div>}
            {detail.directs.map((d) => (
              <Link key={d.id} to={`/admin/users/${d.id}`} className="flex items-center justify-between py-2 text-sm hover:text-white">
                <div><div className="text-white">{d.name}</div><div className="text-xs text-slate-500">{d.email}</div></div>
                <div className="text-xs" style={{color: RANK_COLORS[d.rank]}}>{d.rank} · ${(d.active_investment||0).toFixed(0)}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-3">Recent Transactions</div>
          <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
            {detail.transactions.slice(0, 30).map((t, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="capitalize text-slate-200 text-xs">{t.type.replace("_"," ")}</div>
                  <div className="text-[10px] text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-mono ${t.amount>=0?"text-[#8AE234]":"text-red-400"}`}>{t.amount>=0?"+":""}${Math.abs(t.amount).toFixed(4)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="nb-card p-6 lg:col-span-2">
          <div className="font-heading text-white font-semibold mb-3">Deposits / Withdrawals</div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase text-slate-500 mb-2">Deposits</div>
              <div className="divide-y divide-white/5 max-h-56 overflow-y-auto">
                {detail.deposits.length === 0 && <div className="text-slate-500 text-sm py-2">None</div>}
                {detail.deposits.map((d) => (
                  <div key={d.id} className="flex items-center justify-between py-2 text-sm">
                    <div><div className="font-mono text-white">${d.amount.toFixed(2)} <span className="text-xs text-slate-500">{d.network}</span></div><div className="text-[10px] font-mono text-slate-500 truncate max-w-[180px]">{d.tx_hash}</div></div>
                    <div className={`text-[10px] uppercase px-2 py-0.5 rounded ${d.status==="approved"?"text-[#8AE234] bg-[#8AE234]/10":d.status==="pending"?"text-amber-300 bg-amber-500/10":"text-red-400 bg-red-500/10"}`}>{d.status}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500 mb-2">Withdrawals</div>
              <div className="divide-y divide-white/5 max-h-56 overflow-y-auto">
                {detail.withdrawals.length === 0 && <div className="text-slate-500 text-sm py-2">None</div>}
                {detail.withdrawals.map((w) => (
                  <div key={w.id} className="flex items-center justify-between py-2 text-sm">
                    <div><div className="font-mono text-white">${w.amount.toFixed(2)} <span className="text-xs text-slate-500">{w.network}</span></div><div className="text-[10px] font-mono text-slate-500 truncate max-w-[180px]">{w.wallet_address}</div></div>
                    <div className={`text-[10px] uppercase px-2 py-0.5 rounded ${w.status==="approved"?"text-[#8AE234] bg-[#8AE234]/10":w.status==="pending"?"text-amber-300 bg-amber-500/10":"text-red-400 bg-red-500/10"}`}>{w.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Deposit */}
      <Dialog open={manualDepOpen} onOpenChange={setManualDepOpen}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Manual Deposit</DialogTitle>
            <DialogDescription className="text-slate-400">Credit this user's balance and record it as a deposit (bypasses tx hash verification).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="manual-dep-amount" type="number" min={0.01} step={0.01} value={manualDep.amount} onChange={(e)=>setManualDep({...manualDep, amount: e.target.value})} placeholder="Amount (USDT)" className="nb-input"/>
            <input data-testid="manual-dep-note" value={manualDep.note} onChange={(e)=>setManualDep({...manualDep, note: e.target.value})} placeholder="Note (optional)" className="nb-input"/>
          </div>
          <DialogFooter>
            <button onClick={()=>setManualDepOpen(false)} className="nb-btn-secondary text-sm">Cancel</button>
            <button data-testid="confirm-manual-dep" disabled={!manualDep.amount} onClick={doManualDep} className="nb-btn-primary text-sm">Credit deposit</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance Adjust */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader><DialogTitle>Adjust Balance</DialogTitle><DialogDescription className="text-slate-400">Manually credit or debit this user's balance. A transaction record is created.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <button onClick={()=>setAdjust({...adjust, direction: "credit"})} className={`flex-1 px-3 py-2 rounded-lg border text-sm ${adjust.direction==="credit"?"border-[#8AE234] text-[#8AE234] bg-[#8AE234]/10":"border-white/10 text-slate-300"}`}><Plus size={12} className="inline mr-1"/>Credit</button>
              <button onClick={()=>setAdjust({...adjust, direction: "debit"})} className={`flex-1 px-3 py-2 rounded-lg border text-sm ${adjust.direction==="debit"?"border-red-500 text-red-400 bg-red-500/10":"border-white/10 text-slate-300"}`}><Minus size={12} className="inline mr-1"/>Debit</button>
            </div>
            <input data-testid="adjust-amount" type="number" min={0.01} step={0.01} value={adjust.amount} onChange={(e)=>setAdjust({...adjust, amount: e.target.value})} placeholder="Amount (USDT)" className="nb-input"/>
            <input data-testid="adjust-reason" value={adjust.reason} onChange={(e)=>setAdjust({...adjust, reason: e.target.value})} placeholder="Reason / note" className="nb-input"/>
          </div>
          <DialogFooter>
            <button onClick={()=>setAdjustOpen(false)} className="nb-btn-secondary text-sm">Cancel</button>
            <button data-testid="confirm-adjust" disabled={!adjust.amount} onClick={doAdjust} className="nb-btn-primary text-sm">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Rank */}
      <Dialog open={rankOpen} onOpenChange={setRankOpen}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader><DialogTitle>Set Rank</DialogTitle><DialogDescription className="text-slate-400">Manually override the user's rank tier.</DialogDescription></DialogHeader>
          <select data-testid="rank-select" value={newRank} onChange={(e)=>setNewRank(e.target.value)} className="nb-input">
            <option value="core">Core</option><option value="prime">Prime</option><option value="elite">Elite</option><option value="supreme">Supreme</option>
          </select>
          <DialogFooter>
            <button onClick={()=>setRankOpen(false)} className="nb-btn-secondary text-sm">Cancel</button>
            <button data-testid="confirm-rank" onClick={doRank} className="nb-btn-primary text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Bot */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader><DialogTitle>Reset Bot</DialogTitle><DialogDescription className="text-slate-400">This unlocks the user's bot slot so they can pick a different one. Current investment: ${(u.active_investment||0).toFixed(2)}.</DialogDescription></DialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input data-testid="reset-refund" type="checkbox" checked={refund} onChange={(e)=>setRefund(e.target.checked)} className="accent-[#8AE234]"/>
            Refund investment back to balance
          </label>
          <DialogFooter>
            <button onClick={()=>setResetOpen(false)} className="nb-btn-secondary text-sm">Cancel</button>
            <button data-testid="confirm-reset" onClick={doReset} className="nb-btn-primary text-sm">Reset bot</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={pwOpen} onOpenChange={(o)=>{ setPwOpen(o); if(!o){ setPwResult(null); setPwCustom(""); setPwShow(false);} }}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key size={16} className="text-nb-cyan"/> Reset Password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Passwords are stored one-way encrypted (bcrypt) — the original cannot be recovered. Set a new password here and share it with the user securely. The new password is shown ONCE.
            </DialogDescription>
          </DialogHeader>
          {!pwResult ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Custom password (leave blank to auto-generate)</label>
                <div className="relative mt-1">
                  <input data-testid="pw-custom" type={pwShow?"text":"password"} value={pwCustom} onChange={(e)=>setPwCustom(e.target.value)} placeholder="Leave empty for secure random password" className="nb-input pr-10"/>
                  <button type="button" onClick={()=>setPwShow(!pwShow)} className="absolute right-3 top-3 text-slate-400" data-testid="pw-toggle">{pwShow?<EyeOff size={16}/>:<Eye size={16}/>}</button>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Min 6 characters. Blank = 12-char alphanumeric auto-generated.</div>
              </div>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200">
                ⚠ This action invalidates any existing sessions for {u.email} on next request and clears their login rate-limit.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-[#8AE234]/30 bg-[#8AE234]/5 px-4 py-3">
                <div className="text-xs uppercase tracking-widest text-slate-400">New password for {pwResult.user?.email}</div>
                <div className="mt-2 flex items-center gap-2">
                  <code data-testid="pw-new-value" className="font-mono text-white text-lg bg-black/40 border border-white/10 rounded px-3 py-2 flex-1 break-all select-all">{pwResult.new_password}</code>
                  <button onClick={copyPassword} data-testid="pw-copy" className="nb-btn-secondary text-xs px-3"><Copy size={12} className="inline mr-1"/>Copy</button>
                </div>
                <div className="text-[10px] text-amber-300 mt-2">⚠ This will NOT be shown again. Copy and share securely.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            {!pwResult ? (
              <>
                <button onClick={()=>setPwOpen(false)} className="nb-btn-secondary text-sm">Cancel</button>
                <button data-testid="confirm-pw-reset" disabled={pwBusy} onClick={doPasswordReset} className="nb-btn-primary text-sm">{pwBusy?"Resetting…":"Reset password"}</button>
              </>
            ) : (
              <button onClick={()=>setPwOpen(false)} className="nb-btn-primary text-sm">Done</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function ApprovalTable({ items, onAct, onCorrect, testidPrefix, type }) {
  const isWithdrawal = type === "withdrawal";
  const copyAddr = async (addr) => {
    try {
      await navigator.clipboard.writeText(addr);
      toast.success("Address copied — paste in your wallet");
    } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="nb-card p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
          <tr>
            <th className="text-left px-3 py-3">User</th>
            <th className="text-left px-3 py-3">Network</th>
            <th className="text-right px-3 py-3">Amount</th>
            {isWithdrawal && <th className="text-right px-3 py-3">Net (After Fee)</th>}
            <th className="text-left px-3 py-3">{type === "deposit" ? "TX Hash" : "Wallet Address"}</th>
            <th className="text-left px-3 py-3">Status</th>
            <th className="px-3 py-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {items.map((d) => (
            <tr key={d.id} data-testid={`${testidPrefix}-${d.id}`}>
              <td className="px-3 py-3 text-xs">
                <Link to={`/admin/users/${d.user_id}`} className="hover:text-nb-cyan text-white block max-w-[140px] truncate" data-testid={`${testidPrefix}-user-${d.id}`}>{d.user_name || d.user_id.slice(0,8)+"…"}</Link>
                {d.user_email && <div className="text-[10px] font-mono text-slate-500 truncate max-w-[140px]">{d.user_email}</div>}
              </td>
              <td className="px-3 py-3">{d.network}</td>
              <td className="px-3 py-3 text-right font-mono text-white">${d.amount.toFixed(2)}</td>
              {isWithdrawal && (
                <td className="px-3 py-3 text-right font-mono text-[#8AE234]" data-testid={`${testidPrefix}-net-${d.id}`}>
                  {d.net_amount != null ? `$${d.net_amount.toFixed(2)}` : `$${d.amount.toFixed(2)}`}
                  {d.deduction_pct > 0 && <div className="text-[10px] text-red-300">-{d.deduction_pct}% (${(d.deduction_amount||0).toFixed(2)})</div>}
                </td>
              )}
              <td className="px-3 py-3 text-xs font-mono text-slate-300">
                {isWithdrawal ? (
                  <div className="flex items-center gap-2 min-w-[280px]">
                    <a
                      href={`https://bscscan.com/address/${d.wallet_address}`}
                      target="_blank" rel="noopener noreferrer"
                      className="hover:text-nb-cyan truncate flex-1 whitespace-nowrap overflow-hidden text-ellipsis"
                      title={d.wallet_address}
                      data-testid={`${testidPrefix}-addr-${d.id}`}
                    >{d.wallet_address}</a>
                    <button onClick={()=>copyAddr(d.wallet_address)} data-testid={`${testidPrefix}-copy-${d.id}`} className="shrink-0 p-1.5 rounded bg-nb-cyan/10 text-nb-cyan hover:bg-nb-cyan/20 border border-nb-cyan/20" title="Copy full address">
                      <Copy size={12}/>
                    </button>
                    <a
                      href={`https://link.trustwallet.com/send?asset=c20000714_t${d.wallet_address}&amount=${d.net_amount || d.amount}`}
                      target="_blank" rel="noopener noreferrer"
                      className="shrink-0 p-1.5 rounded bg-[#3375BB]/20 text-[#3375BB] hover:bg-[#3375BB]/30 border border-[#3375BB]/40"
                      title="Open in Trust Wallet"
                      data-testid={`${testidPrefix}-wallet-open-${d.id}`}
                    >
                      <ExternalLink size={12}/>
                    </a>
                  </div>
                ) : (
                  <div className="truncate max-w-[280px]">{d.tx_hash}</div>
                )}
              </td>
              <td className="px-3 py-3 text-xs uppercase">{d.status}</td>
              <td className="px-3 py-3">
                {d.status === "pending" && (
                  <div className="flex gap-1 justify-end flex-wrap">
                    <button data-testid={`approve-${testidPrefix}-${d.id}`} onClick={()=>onAct(d.id, true)} className="text-xs px-2 py-1 rounded bg-[#8AE234]/10 text-[#8AE234] border border-[#8AE234]/30">Approve</button>
                    <button onClick={()=>onAct(d.id, false)} className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/30">Reject</button>
                    {isWithdrawal && onCorrect && (
                      <button data-testid={`correct-${testidPrefix}-${d.id}`} onClick={()=>onCorrect(d)} className="text-xs px-2 py-1 rounded bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30" title="Silently reduce amount & refund the difference to user's balance (deposit-recovery correction)">Correct</button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={isWithdrawal ? 7 : 6} className="text-center py-8 text-slate-500">Nothing to review</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function AdminDeposits() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/deposits"); setItems(data.deposits); };
  useEffect(()=>{ load(); }, []);
  const onAct = async (id, approve) => { try { await api.post("/admin/deposit-approve", { id, approve }); toast.success("Applied"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (<AppLayout variant="admin" requireAdmin><h1 className="font-heading font-bold text-3xl text-white mb-6">Deposits</h1><ApprovalTable items={items} onAct={onAct} testidPrefix="dep" type="deposit"/></AppLayout>);
}

export function AdminWithdrawals() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/withdrawals"); setItems(data.withdrawals); };
  useEffect(()=>{ load(); }, []);
  const onAct = async (id, approve) => { try { await api.post("/admin/withdrawal-approve", { id, approve }); toast.success("Applied"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const onCorrect = async (w) => {
    const suggested = String(w.amount);
    const raw = window.prompt(
      `Silent correction — reduce this pending withdrawal & refund the difference to the user's balance.\n\n` +
      `User: ${w.user_email || w.user_id}\n` +
      `Original amount: $${w.amount.toFixed(2)}\n\n` +
      `Enter the NEW (smaller) amount to keep as pending:`,
      suggested,
    );
    if (raw == null) return;
    const newAmount = parseFloat(String(raw).trim());
    if (isNaN(newAmount) || newAmount < 0 || newAmount >= w.amount) {
      toast.error("Amount must be a positive number less than the original");
      return;
    }
    const refund = (w.amount - newAmount).toFixed(2);
    if (!window.confirm(`Confirm silent correction?\n\nNew pending: $${newAmount.toFixed(2)}\nRefund to user's balance: $${refund}\n\n(No notification will be sent to the user.)`)) return;
    try {
      const { data } = await api.post("/admin/withdrawal-correct-amount", { id: w.id, new_amount: newAmount, note: "Deposit-recovery correction" });
      toast.success(`Corrected: $${data.new_amount.toFixed(2)} pending · $${data.refunded_to_balance.toFixed(2)} refunded`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (<AppLayout variant="admin" requireAdmin><h1 className="font-heading font-bold text-3xl text-white mb-6">Withdrawals</h1><ApprovalTable items={items} onAct={onAct} onCorrect={onCorrect} testidPrefix="wd" type="withdrawal"/></AppLayout>);
}

export function AdminKyc() {
  const [subs, setSubs] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/kyc-submissions"); setSubs(data.submissions); };
  useEffect(()=>{ load(); }, []);
  const act = async (user_id, action) => { try { await api.post("/admin/user-action", { user_id, action }); toast.success("Applied"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <AppLayout variant="admin" requireAdmin>
      <h1 className="font-heading font-bold text-3xl text-white mb-6">KYC Submissions</h1>
      <div className="grid md:grid-cols-2 gap-6">
        {subs.length === 0 && <div className="text-slate-500 col-span-2 text-center py-12">No submissions</div>}
        {subs.map((s) => (
          <div key={s.id} className="nb-card p-5" data-testid={`kyc-sub-${s.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-heading">{s.full_name}</div>
                <div className="text-xs text-slate-500 uppercase">{s.document_type} · {s.status}</div>
              </div>
              {s.status === "submitted" && (
                <div className="flex gap-1">
                  <button data-testid={`kyc-approve-btn-${s.id}`} onClick={()=>act(s.user_id, "approve_kyc")} className="text-xs px-2 py-1 rounded bg-[#8AE234]/10 text-[#8AE234] border border-[#8AE234]/30">Approve</button>
                  <button onClick={()=>act(s.user_id, "reject_kyc")} className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/30">Reject</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <img alt="id" src={s.id_document_b64} className="rounded-lg border border-white/10 h-32 w-full object-cover"/>
              <img alt="selfie" src={s.selfie_b64} className="rounded-lg border border-white/10 h-32 w-full object-cover"/>
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}

export function AdminBots() {
  const [bots, setBots] = useState([]);
  // Feb-21: controlled local edits so admin's typed values stay stable across route
  // switches and refetches. Keyed by bot_id.
  const [edits, setEdits] = useState({});
  const load = async () => {
    const { data } = await api.get("/admin/bots");
    setBots(data.bots);
    // Only prefill edits for bots that don't have local edits yet.
    setEdits((prev) => {
      const next = { ...prev };
      for (const b of data.bots) {
        if (!next[b.id]) {
          next[b.id] = {
            min: String(b.min_pct),
            max: String(b.max_pct),
            fixed: String(b.current_profit_pct ?? ""),
          };
        }
      }
      return next;
    });
  };
  useEffect(()=>{ load(); }, []);
  const setEdit = (bot_id, key, val) => setEdits((prev) => ({ ...prev, [bot_id]: { ...(prev[bot_id] || {}), [key]: val } }));
  const savePct = async (bot_id) => {
    const v = edits[bot_id]?.fixed;
    if (v === "" || v == null) { toast.error("Enter a fixed % first"); return; }
    try { await api.post("/admin/set-bot-pct", { bot_id, profit_pct: parseFloat(v) }); toast.success("Fixed % saved"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveRange = async (bot_id) => {
    const e = edits[bot_id] || {};
    try { await api.post("/admin/set-bot-pct", { bot_id, min_pct: parseFloat(e.min), max_pct: parseFloat(e.max) }); toast.success("Range saved"); await load(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  return (
    <AppLayout variant="admin" requireAdmin>
      <h1 className="font-heading font-bold text-3xl text-white mb-6">Bot Profit Control</h1>
      <div className="grid md:grid-cols-2 gap-6">
        {bots.map((b) => {
          const e = edits[b.id] || {};
          return (
          <div key={b.id} className="nb-card p-6" data-testid={`admin-bot-${b.id}`}>
            <div className="font-heading text-white text-lg">{b.name}</div>
            <div className="text-xs text-slate-500 mt-1">Active range: <span className="text-nb-cyan font-mono">{b.min_pct}%</span> – <span className="text-nb-cyan font-mono">{b.max_pct}%</span> · Live fixed: <span className="text-nb-cyan font-mono">{b.current_profit_pct}%</span></div>

            {/* Feb-2026: editable min/max range */}
            <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-white/5 bg-[#070B14] p-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-widest">Min %</label>
                <input data-testid={`admin-bot-min-${b.id}`} type="number" step="0.01" min={0} max={100} value={e.min ?? ""} onChange={(ev)=>setEdit(b.id, "min", ev.target.value)} className="nb-input font-mono w-full mt-1"/>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-widest">Max %</label>
                <input data-testid={`admin-bot-max-${b.id}`} type="number" step="0.01" min={0} max={100} value={e.max ?? ""} onChange={(ev)=>setEdit(b.id, "max", ev.target.value)} className="nb-input font-mono w-full mt-1"/>
              </div>
              <button data-testid={`admin-bot-save-range-${b.id}`} onClick={()=>saveRange(b.id)} className="col-span-2 nb-btn-secondary text-xs">Save random range</button>
            </div>

            <div className="mt-4">
              <label className="text-[10px] text-slate-500 uppercase tracking-widest">Fixed profit % (locked — overrides random daily pick)</label>
              <div className="mt-1 flex items-center gap-3">
                <input data-testid={`admin-bot-pct-${b.id}`} type="number" step="0.01" min={0} max={100} value={e.fixed ?? ""} onChange={(ev)=>setEdit(b.id, "fixed", ev.target.value)} className="nb-input font-mono flex-1"/>
                <span className="text-xs text-slate-400">%/day</span>
                <button data-testid={`admin-bot-save-pct-${b.id}`} onClick={()=>savePct(b.id)} className="nb-btn-primary text-xs whitespace-nowrap">Save fixed %</button>
              </div>
              <div className="text-[10px] text-amber-300/70 mt-1">Once saved, every daily profit uses this exact %. Value stays until you change it.</div>
            </div>
            <div className="text-xs text-slate-500 mt-3">Random pick uses <span className="text-nb-cyan font-mono">{b.min_pct}%–{b.max_pct}%</span> · Service charge: {b.service_charge}%</div>
          </div>
          );
        })}
      </div>
    </AppLayout>
  );
}

export function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const load = async () => { const { data } = await api.get("/admin/announcements"); setItems(data.announcements); };
  useEffect(()=>{ load(); }, []);
  const post = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/announcements", { title, body }); toast.success("Posted"); setTitle(""); setBody(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/admin/announcements/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <AppLayout variant="admin" requireAdmin>
      <h1 className="font-heading font-bold text-3xl text-white mb-6">Announcements</h1>
      <form onSubmit={post} className="nb-card p-6 space-y-3 mb-6">
        <input data-testid="ann-title" required value={title} onChange={(e)=>setTitle(e.target.value)} className="nb-input" placeholder="Title"/>
        <textarea data-testid="ann-body" required value={body} onChange={(e)=>setBody(e.target.value)} className="nb-input min-h-[100px]" placeholder="Announcement body"/>
        <button data-testid="ann-submit" className="nb-btn-primary">Post</button>
      </form>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="nb-card p-4 flex items-start justify-between">
            <div>
              <div className="text-white font-heading">{a.title}</div>
              <div className="text-sm text-slate-400 mt-1 whitespace-pre-wrap">{a.body}</div>
              <div className="text-xs text-slate-500 mt-2">{new Date(a.created_at).toLocaleString()}</div>
            </div>
            <button data-testid={`ann-delete-${a.id}`} onClick={()=>del(a.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}

export function AdminTransactions() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const url = type === "all" ? "/admin/transactions" : `/admin/transactions?type=${type}`;
    const { data } = await api.get(url); setItems(data.transactions);
  };
  useEffect(() => { load(); }, [type]);
  const filtered = useMemo(() => q ? items.filter(t => (t.user_email||"").toLowerCase().includes(q.toLowerCase()) || (t.user_name||"").toLowerCase().includes(q.toLowerCase())) : items, [items, q]);

  const types = ["all","deposit","withdrawal","daily_profit","referral_bonus","team_income","bot_activation","leadership_bonus","admin_credit","admin_debit","rank_upgrade","bot_reset"];

  return (
    <AppLayout variant="admin" requireAdmin>
      <h1 className="font-heading font-bold text-3xl text-white mb-6">Transaction Audit Log</h1>
      <div className="flex gap-2 mb-4 flex-wrap">
        <select data-testid="tx-type-filter" value={type} onChange={(e)=>setType(e.target.value)} className="nb-input py-2 text-sm">
          {types.map(t => <option key={t} value={t}>{t.replace("_"," ")}</option>)}
        </select>
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-3 text-slate-500"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search by user email or name" className="nb-input pl-9 py-2 text-sm w-full"/>
        </div>
      </div>
      <div className="nb-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-left px-3 py-3">User</th>
              <th className="text-right px-3 py-3">Amount</th>
              <th className="text-left px-3 py-3">Meta</th>
              <th className="text-right px-3 py-3">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No transactions</td></tr>}
            {filtered.slice(0, 300).map((t, i) => (
              <tr key={i}>
                <td className="px-3 py-3 text-xs uppercase tracking-widest text-nb-cyan">{t.type.replace("_"," ")}</td>
                <td className="px-3 py-3">
                  <Link to={`/admin/users/${t.user_id}`} className="hover:text-nb-cyan">
                    <div className="text-white text-sm">{t.user_name||"—"}</div>
                    <div className="text-xs text-slate-500">{t.user_email}</div>
                  </Link>
                </td>
                <td className={`px-3 py-3 text-right font-mono ${t.amount>=0?"text-[#8AE234]":"text-red-400"}`}>{t.amount>=0?"+":""}${Math.abs(t.amount).toFixed(4)}</td>
                <td className="px-3 py-3 text-xs font-mono text-slate-400 truncate max-w-[240px]">{t.meta ? Object.entries(t.meta).map(([k,v])=>`${k}:${typeof v === "number" ? v.toFixed?.(2) ?? v : v}`).join(" · ") : "—"}</td>
                <td className="px-3 py-3 text-right text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

/* Wallet address editor with 3-email OTP protection (Feb 2026) */
function WalletAddressEditor({ cfg, onSaved }) {
  const [bep20, setBep20] = useState(cfg?.bep20_wallet || "");
  const [trc20, setTrc20] = useState(cfg?.trc20_wallet || "");
  const [challenge, setChallenge] = useState(null); // { field, new_address, challenge_id, sent_count }
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setBep20(cfg?.bep20_wallet || ""); setTrc20(cfg?.trc20_wallet || ""); }, [cfg?.bep20_wallet, cfg?.trc20_wallet]);

  const requestOtp = async (field, new_address) => {
    if (!new_address || new_address === cfg[field]) { toast.info("Enter a new address different from current."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet-change/request-otp", { field, new_address });
      setChallenge({ field, new_address, challenge_id: data.challenge_id, sent_count: data.sent_count });
      toast.success(`Same OTP sent to ${data.sent_count} authorized emails`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!otp) { toast.error("Enter the OTP from any authorized email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wallet-change/verify", { challenge_id: challenge.challenge_id, otp: otp.trim() });
      toast.success(data.message);
      setChallenge(null); setOtp("");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="nb-card p-6" data-testid="cfg-wallet-block">
      <div className="font-heading text-white font-semibold mb-4">Company USDT Wallets</div>
      <div className="text-[11px] text-amber-300/70 mb-3">🔒 Wallet address changes require OTP confirmation from any of the 3 authorized security emails.</div>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400">TRC20 Address</label>
          <input data-testid="cfg-trc20" value={trc20} onChange={(e)=>setTrc20(e.target.value)} className="nb-input mt-1 font-mono text-sm"/>
          <button data-testid="wallet-otp-trc20" disabled={busy || trc20 === cfg?.trc20_wallet} onClick={()=>requestOtp("trc20_wallet", trc20)} className="nb-btn-secondary text-xs mt-2">Request OTP to change TRC20</button>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400">BEP20 Address</label>
          <input data-testid="cfg-bep20" value={bep20} onChange={(e)=>setBep20(e.target.value)} className="nb-input mt-1 font-mono text-sm"/>
          <button data-testid="wallet-otp-bep20" disabled={busy || bep20 === cfg?.bep20_wallet} onClick={()=>requestOtp("bep20_wallet", bep20)} className="nb-btn-secondary text-xs mt-2">Request OTP to change BEP20</button>
        </div>
      </div>
      {challenge && (
        <div className="mt-4 rounded-xl border border-nb-cyan/30 bg-nb-cyan/5 p-4 space-y-3" data-testid="wallet-otp-panel">
          <div className="text-xs text-slate-300">
            Same OTP sent to <span className="text-nb-cyan font-mono">{challenge.sent_count}</span> authorized emails.
            Confirming change of <span className="text-white font-mono">{challenge.field}</span>.
          </div>
          <input data-testid="wallet-otp-input" value={otp} onChange={(e)=>setOtp(e.target.value)} inputMode="numeric" maxLength={6} className="nb-input font-mono tracking-widest text-center text-lg" placeholder="••••••"/>
          <div className="flex gap-2">
            <button data-testid="wallet-otp-confirm" disabled={busy} onClick={confirm} className="nb-btn-primary text-sm flex-1">Confirm</button>
            <button onClick={()=>{ setChallenge(null); setOtp(""); }} className="nb-btn-secondary text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/config"); setCfg(data.config); };
  useEffect(() => { load(); }, []);
  const save = async (updates) => {
    setSaving(true);
    try { const { data } = await api.post("/admin/config", updates); setCfg(data.config); toast.success("Config updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!cfg) return <AppLayout variant="admin" requireAdmin><div className="text-slate-500">Loading…</div></AppLayout>;

  const setField = (k, v) => setCfg({...cfg, [k]: v});
  const setLevel = (idx, v) => { const lv = [...cfg.level_income_pct]; lv[idx] = parseFloat(v)||0; setCfg({...cfg, level_income_pct: lv}); };
  const setTier = (idx, field, v) => { const tiers = cfg.rank_tiers.map((t,i) => i===idx ? {...t, [field]: field==="key"||field==="label"||field==="color" ? v : parseFloat(v)||0} : t); setCfg({...cfg, rank_tiers: tiers}); };

  return (
    <AppLayout variant="admin" requireAdmin>
      <h1 className="font-heading font-bold text-3xl text-white mb-6">System Configuration</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">Deposit / Withdrawal</div>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Minimum Deposit ($)</label>
              <input data-testid="cfg-min-deposit" type="number" min={1} value={cfg.min_deposit} onChange={(e)=>setField("min_deposit", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Minimum Withdrawal ($)</label>
              <input data-testid="cfg-min-withdrawal" type="number" min={1} value={cfg.min_withdrawal} onChange={(e)=>setField("min_withdrawal", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Withdrawal Fee ($)</label>
              <input data-testid="cfg-wd-fee" type="number" min={0} step="0.1" value={cfg.withdrawal_fee} onChange={(e)=>setField("withdrawal_fee", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Withdrawal Deduction %</label>
              <input data-testid="cfg-wd-deduction-pct" type="number" min={0} max={100} step="0.1" value={cfg.withdrawal_deduction_pct || 0} onChange={(e)=>setField("withdrawal_deduction_pct", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
              <div className="text-xs text-slate-500 mt-1">Deducted from user's gross withdrawal. Net = gross × (1 − X%).</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Minimum Activation Amount ($)</label>
              <input data-testid="cfg-min-activation" type="number" min={0} step="0.1" value={cfg.min_activation_amount || 10} onChange={(e)=>setField("min_activation_amount", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
              <div className="text-xs text-slate-500 mt-1">Accounts become active once total deposit ≥ this amount.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Withdrawal Cooldown (hours)</label>
              <select data-testid="cfg-wd-cooldown-hours" value={cfg.withdrawal_cooldown_hours ?? 24} onChange={(e)=>setField("withdrawal_cooldown_hours", parseFloat(e.target.value)||0)} className="nb-input mt-1">
                <option value={24}>Daily · 24 hours</option>
                <option value={48}>Every 2 days · 48 hours</option>
                <option value={72}>Every 3 days · 72 hours</option>
                <option value={96}>Every 4 days · 96 hours</option>
                <option value={120}>Every 5 days · 120 hours</option>
                <option value={168}>Weekly · 168 hours</option>
                <option value={0}>Disabled · No cooldown</option>
              </select>
              <div className="text-xs text-slate-500 mt-1">Time between two withdrawal requests. Timer starts from the user's last withdrawal.</div>
            </div>
          </div>
          <button data-testid="save-deposit-cfg" disabled={saving} onClick={()=>save({min_deposit: cfg.min_deposit, min_withdrawal: cfg.min_withdrawal, withdrawal_fee: cfg.withdrawal_fee, withdrawal_deduction_pct: cfg.withdrawal_deduction_pct, min_activation_amount: cfg.min_activation_amount, withdrawal_cooldown_hours: cfg.withdrawal_cooldown_hours})} className="nb-btn-primary text-sm mt-4">Save</button>
        </div>

        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">On-Chain Verification (BSC)</div>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Verification Mode</label>
              <select data-testid="cfg-verify-mode" value={cfg.deposit_verification_mode || "simple"} onChange={(e)=>setField("deposit_verification_mode", e.target.value)} className="nb-input mt-1">
                <option value="simple">Simple (unique tx hash only) — MOCK</option>
                <option value="onchain">On-chain (real BscScan verification) — LIVE</option>
              </select>
              <div className="text-xs text-slate-500 mt-1">Simple: accepts any unique hash. On-chain: queries BscScan for real USDT transfer to your wallet.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">BscScan API Key</label>
              <input data-testid="cfg-bscscan-key" type="password" value={cfg.bscscan_api_key || ""} onChange={(e)=>setField("bscscan_api_key", e.target.value)} className="nb-input mt-1 font-mono text-xs" placeholder="Paste from bscscan.com/myapi"/>
              <div className="text-xs text-slate-500 mt-1">Required for on-chain mode. Free tier gives 5 req/sec. <a href="https://bscscan.com/myapi" target="_blank" rel="noreferrer" className="text-nb-cyan hover:underline">Get key →</a></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Min Confirmations</label>
                <input data-testid="cfg-min-confirmations" type="number" min={1} max={30} value={cfg.deposit_min_confirmations || 3} onChange={(e)=>setField("deposit_min_confirmations", parseInt(e.target.value)||3)} className="nb-input mt-1"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Amount Tolerance %</label>
                <input data-testid="cfg-tolerance" type="number" min={0} max={20} step="0.1" value={cfg.deposit_amount_tolerance_pct || 1} onChange={(e)=>setField("deposit_amount_tolerance_pct", parseFloat(e.target.value)||1)} className="nb-input mt-1"/>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">USDT BEP20 Contract</label>
              <input data-testid="cfg-usdt-contract" value={cfg.usdt_bep20_contract || ""} onChange={(e)=>setField("usdt_bep20_contract", e.target.value)} className="nb-input mt-1 font-mono text-xs"/>
              <div className="text-xs text-slate-500 mt-1">Default: 0x55d398326f99059fF775485246999027B3197955 (official USDT on BSC)</div>
            </div>
          </div>
          <button data-testid="save-onchain-cfg" disabled={saving} onClick={()=>save({deposit_verification_mode: cfg.deposit_verification_mode, bscscan_api_key: cfg.bscscan_api_key, deposit_min_confirmations: cfg.deposit_min_confirmations, deposit_amount_tolerance_pct: cfg.deposit_amount_tolerance_pct, usdt_bep20_contract: cfg.usdt_bep20_contract})} className="nb-btn-primary text-sm mt-4">Save</button>
        </div>

        <WalletAddressEditor cfg={cfg} onSaved={load}/>

        <div className="nb-card p-6" data-testid="cfg-security-block">
          <div className="font-heading text-white font-semibold mb-4">Admin Security (3-Email OTP)</div>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input data-testid="cfg-admin-otp-enabled" type="checkbox" checked={!!cfg.admin_login_otp_enabled} onChange={(e)=>setField("admin_login_otp_enabled", e.target.checked)}/>
              Require OTP for admin login
            </label>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Authorized Security Emails (any 1 OTP unlocks login/wallet-change)</label>
              {(cfg.admin_otp_emails || ["", "", ""]).map((em, i) => (
                <input key={i} data-testid={`cfg-admin-otp-email-${i}`} type="email" value={em} onChange={(e)=>{ const arr = [...(cfg.admin_otp_emails||["","",""])]; arr[i] = e.target.value; setField("admin_otp_emails", arr); }} className="nb-input mt-2 font-mono text-xs" placeholder={`security-email-${i+1}@example.com`}/>
              ))}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Telegram Support URL (shown on user dashboard)</label>
              <input data-testid="cfg-telegram-url" value={cfg.telegram_support_url || ""} onChange={(e)=>setField("telegram_support_url", e.target.value)} className="nb-input mt-1 font-mono text-xs" placeholder="https://t.me/yourgroup"/>
            </div>
          </div>
          <button data-testid="save-security-cfg" disabled={saving} onClick={()=>save({admin_login_otp_enabled: cfg.admin_login_otp_enabled, admin_otp_emails: cfg.admin_otp_emails, telegram_support_url: cfg.telegram_support_url})} className="nb-btn-primary text-sm mt-4">Save</button>
        </div>

        <div className="nb-card p-6" data-testid="cfg-tour-block">
          <div className="font-heading text-white font-semibold mb-4">Tour Eligibility Thresholds</div>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Domestic Shareholder Threshold ($)</label>
              <input data-testid="cfg-tour-domestic" type="number" min={0} step="1" value={cfg.tour_domestic_shareholder_usd || 2000} onChange={(e)=>setField("tour_domestic_shareholder_usd", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">International Team Business Threshold ($)</label>
              <input data-testid="cfg-tour-international" type="number" min={0} step="1" value={cfg.tour_international_team_business_usd || 20000} onChange={(e)=>setField("tour_international_team_business_usd", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Leadership Min Active Directs</label>
                <input data-testid="cfg-leadership-min" type="number" min={1} step="1" value={cfg.leadership_min_active_directs || 10} onChange={(e)=>setField("leadership_min_active_directs", parseInt(e.target.value)||10)} className="nb-input mt-1"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Leadership Bonus %</label>
                <input data-testid="cfg-leadership-pct" type="number" min={0} max={100} step="0.1" value={cfg.leadership_bonus_pct || 10} onChange={(e)=>setField("leadership_bonus_pct", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
              </div>
            </div>
          </div>
          <button data-testid="save-tour-cfg" disabled={saving} onClick={()=>save({tour_domestic_shareholder_usd: cfg.tour_domestic_shareholder_usd, tour_international_team_business_usd: cfg.tour_international_team_business_usd, leadership_min_active_directs: cfg.leadership_min_active_directs, leadership_bonus_pct: cfg.leadership_bonus_pct})} className="nb-btn-primary text-sm mt-4">Save</button>
          <div className="text-[11px] text-slate-500 mt-2">Domestic = Leadership OR (Total Balance ≥ shareholder threshold). International = Diamond Shareholder OR (Shareholder + team business ≥ threshold).</div>
        </div>

        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">Referral & Team Income</div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Direct Referral Bonus %</label>
            <input data-testid="cfg-direct-pct" type="number" min={0} max={100} step="0.5" value={cfg.direct_referral_bonus_pct} onChange={(e)=>setField("direct_referral_bonus_pct", parseFloat(e.target.value)||0)} className="nb-input mt-1"/>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-slate-400">Level Income % (L1 → L5)</label>
            <div className="grid grid-cols-5 gap-2 mt-1">
              {cfg.level_income_pct.map((v, i) => (
                <input key={i} data-testid={`cfg-level-${i+1}`} type="number" min={0} max={100} step="0.5" value={v} onChange={(e)=>setLevel(i, e.target.value)} className="nb-input font-mono text-center"/>
              ))}
            </div>
          </div>
          <button data-testid="save-referral-cfg" disabled={saving} onClick={()=>save({direct_referral_bonus_pct: cfg.direct_referral_bonus_pct, level_income_pct: cfg.level_income_pct})} className="nb-btn-primary text-sm mt-4">Save</button>
          <div className="text-xs text-amber-300/70 mt-3">Note: MVP daily profit engine uses hard-coded level %. Persisted here for reference; wire-up planned.</div>
        </div>

        <div className="nb-card p-6">
          <div className="font-heading text-white font-semibold mb-4">Rank Tiers</div>
          <div className="space-y-3">
            {cfg.rank_tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 items-center">
                <div className="text-sm font-bold uppercase" style={{color: t.color}}>{t.label}</div>
                <input data-testid={`cfg-tier-self-${t.key}`} type="number" min={0} value={t.self_min} onChange={(e)=>setTier(i, "self_min", e.target.value)} className="nb-input py-1.5 font-mono text-xs" placeholder="Self min $"/>
                <input data-testid={`cfg-tier-team-${t.key}`} type="number" min={0} value={t.team_min} onChange={(e)=>setTier(i, "team_min", e.target.value)} className="nb-input py-1.5 font-mono text-xs" placeholder="Team min $"/>
                <input data-testid={`cfg-tier-lead-${t.key}`} type="number" min={0} max={100} value={t.leadership_pct} onChange={(e)=>setTier(i, "leadership_pct", e.target.value)} className="nb-input py-1.5 font-mono text-xs" placeholder="Leadership %"/>
              </div>
            ))}
            <div className="grid grid-cols-4 gap-2 text-[10px] text-slate-500 uppercase tracking-widest">
              <div>Tier</div><div>Self min $</div><div>Team min $</div><div>Leadership %</div>
            </div>
          </div>
          <button data-testid="save-rank-cfg" disabled={saving} onClick={()=>save({rank_tiers: cfg.rank_tiers})} className="nb-btn-primary text-sm mt-4">Save</button>
          <div className="text-xs text-amber-300/80 mt-3 leading-relaxed">
            <span className="font-bold">Leadership Rule (Feb 2026):</span> Per-tier <span className="font-mono">Leadership %</span> is deprecated. Every qualified user (Active Bot + 10 active directs) receives a <span className="font-bold text-neon-lime">unified 10%</span> of their team's monthly <span className="font-bold">TOTAL income</span> (all types EXCEPT staking), regardless of rank. Configure via the global <span className="font-mono">leadership_bonus_pct</span> field.
          </div>
        </div>

        {/* Staking Plans editor */}
        <div className="nb-card p-6" data-testid="cfg-staking-block">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-white font-semibold">Staking Plans</div>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input data-testid="cfg-staking-enabled" type="checkbox" checked={!!cfg.staking_enabled} onChange={(e)=>setField("staking_enabled", e.target.checked)}/>
              Enabled
            </label>
          </div>
          <div className="grid grid-cols-6 gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            <div>ID</div><div className="col-span-2">Name</div><div>Min $</div><div>Max $</div><div>Multiplier ×</div>
          </div>
          <div className="space-y-2">
            {(cfg.staking_plans || []).map((p, i) => (
              <div key={p.id} className="grid grid-cols-6 gap-2 items-center">
                <div className="font-mono text-nb-cyan">{p.id}</div>
                <input data-testid={`cfg-stake-name-${p.id}`} className="nb-input py-1.5 text-xs col-span-2" value={p.name || ""} onChange={(e)=>{ const arr=[...cfg.staking_plans]; arr[i]={...arr[i], name: e.target.value}; setField("staking_plans", arr); }}/>
                <input data-testid={`cfg-stake-min-${p.id}`} type="number" min={0} className="nb-input py-1.5 font-mono text-xs" value={p.min_amount} onChange={(e)=>{ const arr=[...cfg.staking_plans]; arr[i]={...arr[i], min_amount: parseFloat(e.target.value)||0}; setField("staking_plans", arr); }}/>
                <input data-testid={`cfg-stake-max-${p.id}`} type="number" min={0} className="nb-input py-1.5 font-mono text-xs" value={p.max_amount} onChange={(e)=>{ const arr=[...cfg.staking_plans]; arr[i]={...arr[i], max_amount: parseFloat(e.target.value)||0}; setField("staking_plans", arr); }}/>
                <input data-testid={`cfg-stake-mult-${p.id}`} type="number" min={1} step="0.1" className="nb-input py-1.5 font-mono text-xs text-[#8AE234] font-bold" value={p.multiplier} onChange={(e)=>{ const arr=[...cfg.staking_plans]; arr[i]={...arr[i], multiplier: parseFloat(e.target.value)||1}; setField("staking_plans", arr); }}/>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-slate-400">Duration (days) — applies to all plans</label>
            <input data-testid="cfg-staking-duration" type="number" min={1} className="nb-input py-1.5 font-mono text-xs mt-1 w-32" value={cfg.staking_duration_days || 365} onChange={(e)=>setField("staking_duration_days", parseInt(e.target.value)||365)}/>
          </div>
          <button data-testid="save-staking-cfg" disabled={saving} onClick={()=>save({staking_enabled: cfg.staking_enabled, staking_duration_days: cfg.staking_duration_days, staking_plans: cfg.staking_plans})} className="nb-btn-primary text-sm mt-4">Save Staking Config</button>
          <div className="text-[11px] text-amber-300/70 mt-2">Multiplier = TOTAL RETURN. e.g. 2.5× on $100 → user receives $250 at unlock. Existing stakes keep the multiplier they were locked with.</div>
        </div>

        {/* Shareholder Plans editor */}
        <div className="nb-card p-6" data-testid="cfg-shareholder-block">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-white font-semibold">Company Shareholder Program</div>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input data-testid="cfg-shareholder-enabled" type="checkbox" checked={!!cfg.shareholder_enabled} onChange={(e)=>setField("shareholder_enabled", e.target.checked)}/>
              Enabled
            </label>
          </div>
          <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            <div>ID</div><div>Name</div><div>Required Balance $</div><div>Monthly Bonus %</div>
          </div>
          <div className="space-y-2">
            {(cfg.shareholder_plans || []).map((p, i) => (
              <div key={p.id} className="grid grid-cols-4 gap-2 items-center">
                <div className="font-mono text-nb-cyan">{p.id}</div>
                <input data-testid={`cfg-sh-name-${p.id}`} className="nb-input py-1.5 text-xs" value={p.name || ""} onChange={(e)=>{ const arr=[...cfg.shareholder_plans]; arr[i]={...arr[i], name: e.target.value}; setField("shareholder_plans", arr); }}/>
                <input data-testid={`cfg-sh-min-${p.id}`} type="number" min={0} className="nb-input py-1.5 font-mono text-xs" value={p.min_balance} onChange={(e)=>{ const arr=[...cfg.shareholder_plans]; arr[i]={...arr[i], min_balance: parseFloat(e.target.value)||0}; setField("shareholder_plans", arr); }}/>
                <input data-testid={`cfg-sh-pct-${p.id}`} type="number" min={0} max={100} step="0.1" className="nb-input py-1.5 font-mono text-xs text-[#8AE234] font-bold" value={p.monthly_bonus_pct} onChange={(e)=>{ const arr=[...cfg.shareholder_plans]; arr[i]={...arr[i], monthly_bonus_pct: parseFloat(e.target.value)||0}; setField("shareholder_plans", arr); }}/>
              </div>
            ))}
          </div>
          <button data-testid="save-shareholder-cfg" disabled={saving} onClick={()=>save({shareholder_enabled: cfg.shareholder_enabled, shareholder_plans: cfg.shareholder_plans})} className="nb-btn-primary text-sm mt-4">Save Shareholder Config</button>
          <div className="text-[11px] text-amber-300/70 mt-2">Bonus = (Member's month-min balance + Team active investment volume) × bonus %. Paid once per month, only if user maintained the required balance for the entire month.</div>
        </div>

        {/* Feb-21: Down-level trading income (0.5% floor, admin-editable middle levels) */}
        <div className="nb-card p-6" data-testid="cfg-level-income-block">
          <div className="font-heading text-white font-semibold mb-2">Down-Level Trading Income</div>
          <div className="text-[11px] text-slate-500 mb-3">L1 (Top) fixed at highest. Middle levels (L2-L5) admin-editable. L6+ pays flat 0.5% unlimited depth.</div>
          <div className="grid grid-cols-5 gap-2">
            {(cfg.level_income_pcts || [10,5,3,2,1]).map((v, i) => (
              <div key={i}>
                <label className="text-[10px] uppercase tracking-widest text-slate-500">L{i+1} %</label>
                <input data-testid={`cfg-level-pct-${i+1}`} type="number" min={0} max={100} step="0.1" value={v} onChange={(e)=>{
                  const arr = [...(cfg.level_income_pcts || [10,5,3,2,1])];
                  arr[i] = parseFloat(e.target.value) || 0;
                  setField("level_income_pcts", arr);
                }} className="nb-input py-1.5 font-mono text-center text-sm"/>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Level 6+ % (unlimited depth · Down-Level floor)</label>
            <input data-testid="cfg-level-6plus-pct" type="number" min={0} max={5} step="0.05" value={cfg.level_6plus_pct ?? 0.5} onChange={(e)=>setField("level_6plus_pct", parseFloat(e.target.value)||0)} className="nb-input mt-1 font-mono w-32"/>
          </div>
          <button data-testid="save-level-income-cfg" disabled={saving} onClick={()=>save({level_income_pcts: cfg.level_income_pcts, level_6plus_pct: cfg.level_6plus_pct})} className="nb-btn-primary text-sm mt-4">Save Down-Level Config</button>
        </div>

        {/* Feb-21: Leadership retention rule — 3 new directs per 30 days */}
        <div className="nb-card p-6" data-testid="cfg-leadership-retention-block">
          <div className="font-heading text-white font-semibold mb-2">Leadership Retention Rule</div>
          <div className="text-[11px] text-slate-500 mb-3">Once initially qualified, users must add N new qualifying directs every D days to keep leadership bonus.</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">New Directs Required</label>
              <input data-testid="cfg-lead-new-directs" type="number" min={0} value={cfg.leadership_new_directs_per_period ?? 3} onChange={(e)=>setField("leadership_new_directs_per_period", parseInt(e.target.value)||0)} className="nb-input mt-1 font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Rolling Window (days)</label>
              <input data-testid="cfg-lead-period-days" type="number" min={1} value={cfg.leadership_period_days ?? 30} onChange={(e)=>setField("leadership_period_days", parseInt(e.target.value)||30)} className="nb-input mt-1 font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Direct Min Active Balance ($)</label>
              <input data-testid="cfg-lead-min-bal" type="number" min={0} value={cfg.leadership_direct_min_balance ?? 100} onChange={(e)=>setField("leadership_direct_min_balance", parseFloat(e.target.value)||0)} className="nb-input mt-1 font-mono"/>
            </div>
          </div>
          <button data-testid="save-leadership-retention-cfg" disabled={saving} onClick={()=>save({leadership_new_directs_per_period: cfg.leadership_new_directs_per_period, leadership_period_days: cfg.leadership_period_days, leadership_direct_min_balance: cfg.leadership_direct_min_balance})} className="nb-btn-primary text-sm mt-4">Save Retention Config</button>
        </div>

        {/* Feb-21: Credit Line Slabs + 10X + 50/50 rule */}
        <div className="nb-card p-6 lg:col-span-2" data-testid="cfg-credit-slabs-block">
          <div className="flex items-center justify-between mb-2">
            <div className="font-heading text-white font-semibold">Company Credit Line — Slabs & 10X Rule</div>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input data-testid="cfg-credit-enabled" type="checkbox" checked={!!cfg.credit_enabled} onChange={(e)=>setField("credit_enabled", e.target.checked)}/>
              Enabled
            </label>
          </div>
          <div className="text-[11px] text-slate-500 mb-3">Users must pick an EXACT slab amount. Business target per slab = amount × multiplier (default 10X). Split rule = self+direct must supply half the target.</div>
          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Default Multiplier (X)</label>
              <input data-testid="cfg-credit-multiplier" type="number" min={1} step="0.5" value={cfg.credit_business_multiplier ?? 10} onChange={(e)=>setField("credit_business_multiplier", parseFloat(e.target.value)||10)} className="nb-input mt-1 font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Self+Direct Share %</label>
              <input data-testid="cfg-credit-share-pct" type="number" min={0} max={100} step="1" value={cfg.credit_self_direct_share_pct ?? 50} onChange={(e)=>setField("credit_self_direct_share_pct", parseFloat(e.target.value)||50)} className="nb-input mt-1 font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Validity (days)</label>
              <input data-testid="cfg-credit-validity" type="number" min={1} value={cfg.credit_validity_days ?? 60} onChange={(e)=>setField("credit_validity_days", parseInt(e.target.value)||60)} className="nb-input mt-1 font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Repay Ratio</label>
              <input data-testid="cfg-credit-repay-ratio" type="number" min={0} step="0.1" value={cfg.credit_repayment_ratio ?? 1} onChange={(e)=>setField("credit_repayment_ratio", parseFloat(e.target.value)||1)} className="nb-input mt-1 font-mono"/>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            <div>#</div><div>Amount $</div><div>Multiplier X</div><div>Target $</div>
          </div>
          <div className="space-y-2" data-testid="cfg-credit-slabs-list">
            {(cfg.credit_slabs || []).map((s, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 items-center">
                <div className="font-mono text-nb-cyan">{i+1}</div>
                <input data-testid={`cfg-slab-amt-${i}`} type="number" min={1} className="nb-input py-1.5 font-mono text-xs" value={s.amount} onChange={(e)=>{ const arr=[...cfg.credit_slabs]; arr[i]={...arr[i], amount: parseFloat(e.target.value)||0}; setField("credit_slabs", arr); }}/>
                <input data-testid={`cfg-slab-mult-${i}`} type="number" min={1} step="0.5" className="nb-input py-1.5 font-mono text-xs" value={s.business_multiplier || cfg.credit_business_multiplier || 10} onChange={(e)=>{ const arr=[...cfg.credit_slabs]; arr[i]={...arr[i], business_multiplier: parseFloat(e.target.value)||10}; setField("credit_slabs", arr); }}/>
                <div className="flex items-center gap-2">
                  <div className="font-mono text-[#8AE234] text-xs flex-1">${((s.amount||0) * (s.business_multiplier || cfg.credit_business_multiplier || 10)).toFixed(0)}</div>
                  <button data-testid={`cfg-slab-remove-${i}`} onClick={()=>{ const arr=[...cfg.credit_slabs]; arr.splice(i,1); setField("credit_slabs", arr); }} className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded border border-red-500/30">Remove</button>
                </div>
              </div>
            ))}
          </div>
          <button data-testid="cfg-slab-add" onClick={()=>setField("credit_slabs", [...(cfg.credit_slabs||[]), {amount: 100, business_multiplier: cfg.credit_business_multiplier || 10}])} className="mt-3 text-xs px-3 py-1.5 rounded border border-nb-cyan/40 text-nb-cyan hover:bg-nb-cyan/10"><Plus size={10} className="inline mr-1"/>Add slab</button>
          <button data-testid="save-credit-slabs-cfg" disabled={saving} onClick={()=>save({credit_enabled: cfg.credit_enabled, credit_slabs: cfg.credit_slabs, credit_business_multiplier: cfg.credit_business_multiplier, credit_self_direct_share_pct: cfg.credit_self_direct_share_pct, credit_validity_days: cfg.credit_validity_days, credit_repayment_ratio: cfg.credit_repayment_ratio})} className="nb-btn-primary text-sm mt-4 ml-3">Save Credit Config</button>
        </div>

        {/* Feb-21: Tour Fund Amounts */}
        <div className="nb-card p-6" data-testid="cfg-tour-fund-block">
          <div className="flex items-center justify-between mb-2">
            <div className="font-heading text-white font-semibold">Tour Rewards Fund</div>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input data-testid="cfg-tour-fund-enabled" type="checkbox" checked={!!cfg.tour_fund_enabled} onChange={(e)=>setField("tour_fund_enabled", e.target.checked)}/>
              Enabled
            </label>
          </div>
          <div className="text-[11px] text-slate-500 mb-3">Amount awarded when a user hits Domestic / International tour eligibility. Withdrawal requires an Admin toggle per user in the Tour Funds page.</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Domestic Fund Amount ($)</label>
              <input data-testid="cfg-tour-dom-amount" type="number" min={0} step="1" value={cfg.tour_domestic_fund_amount ?? 500} onChange={(e)=>setField("tour_domestic_fund_amount", parseFloat(e.target.value)||0)} className="nb-input mt-1 font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">International Fund Amount ($)</label>
              <input data-testid="cfg-tour-intl-amount" type="number" min={0} step="1" value={cfg.tour_international_fund_amount ?? 2000} onChange={(e)=>setField("tour_international_fund_amount", parseFloat(e.target.value)||0)} className="nb-input mt-1 font-mono"/>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button data-testid="save-tour-fund-cfg" disabled={saving} onClick={()=>save({tour_fund_enabled: cfg.tour_fund_enabled, tour_domestic_fund_amount: cfg.tour_domestic_fund_amount, tour_international_fund_amount: cfg.tour_international_fund_amount})} className="nb-btn-primary text-sm">Save</button>
            <Link to="/admin/tour-funds" data-testid="link-admin-tour-funds" className="nb-btn-secondary text-sm">Manage Tour Funds →</Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
